/**
 * Freezes the given value in non-production environments to catch accidental
 * mutation of shared default values during development and testing.
 */
const freezeInDev = <T extends object>(value: T): T => {
	if (process.env.NODE_ENV !== 'production') {
		Object.freeze(value);
	}

	return value;
};

/**
 * Shape of the shared `defaultProps` registry of stable, reusable default values.
 */
type DefaultProps = {
	emptyArray: never[];
	emptyObject: Record<string, never>;
	// eslint-disable-next-line jira/react/handler-naming
	noop: () => void;
	// eslint-disable-next-line jira/react/handler-naming
	returnNull: () => null;
	// eslint-disable-next-line jira/react/handler-naming
	returnResolvedPromise: () => Promise<void>;
};

const defaultPropsResolvedPromise = Promise.resolve();

const EMPTY_ARRAY: never[] = freezeInDev([]);
const EMPTY_OBJECT: Record<string, never> = freezeInDev({});
const NOOP: () => void = freezeInDev((): void => undefined);
const RETURN_NULL: () => null = freezeInDev((): null => null);
const RETURN_RESOLVED_PROMISE: () => Promise<void> = freezeInDev(
	(): Promise<void> => defaultPropsResolvedPromise,
);

/**
 * Stable, referentially-equal default values intended for use as component
 * prop defaults. Using these avoids creating new array/object/function
 * instances on every render, which would otherwise break memoization and
 * trigger unnecessary re-renders.
 *
 * All values are frozen in non-production builds to guard against mutation.
 */
export const defaultProps: DefaultProps = freezeInDev({
	emptyArray: EMPTY_ARRAY,
	emptyObject: EMPTY_OBJECT,
	noop: NOOP,
	returnNull: RETURN_NULL,
	returnResolvedPromise: RETURN_RESOLVED_PROMISE,
});
